import { Check } from 'lucide-react';
import type { Key, ReactNode } from 'react';
import {
  Checkbox as AriaCheckbox,
  CheckboxGroup as AriaCheckboxGroup,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-aria-components';
import { cx } from './cx';
import styles from './Field.module.css';
import { FieldHelp, FieldLabel, type FieldChromeProps } from './TextField';

export interface CheckboxProps {
  /** Standalone use: the checked state and its change. Inside a CheckboxGroup, pass `value`. */
  isSelected?: boolean;
  onChange?: (selected: boolean) => void;
  /** The value this box adds to its CheckboxGroup. */
  value?: string;
  children: ReactNode;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  className?: string;
  fieldName?: string;
  testId?: string;
}

export function Checkbox({
  isSelected,
  onChange,
  value,
  children,
  isDisabled,
  isReadOnly,
  className,
  fieldName,
  testId,
}: CheckboxProps) {
  return (
    <AriaCheckbox
      {...(value !== undefined ? { value } : { isSelected: isSelected ?? false })}
      {...(onChange ? { onChange } : {})}
      isDisabled={isDisabled ?? false}
      isReadOnly={isReadOnly ?? false}
      className={cx(styles.check, className)}
      data-field={fieldName}
      data-testid={testId}
    >
      {({ isSelected: selected }) => (
        <>
          <span className={styles.checkBox} aria-hidden="true">
            {selected ? <Check size={13} strokeWidth={3} /> : null}
          </span>
          <span className={styles.checkText}>{children}</span>
        </>
      )}
    </AriaCheckbox>
  );
}

export interface CheckboxGroupProps {
  /** Visible group name (read by screen readers with each box). */
  label: ReactNode;
  value: string[];
  onChange: (value: string[]) => void;
  children: ReactNode;
  description?: ReactNode;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  className?: string;
  listClassName?: string;
  fieldName?: string;
  testId?: string;
}

/** Several independent choices under one name (list facets, an item's default taxes). */
export function CheckboxGroup({
  label,
  value,
  onChange,
  children,
  description,
  isReadOnly,
  isDisabled,
  className,
  listClassName,
  fieldName,
  testId,
}: CheckboxGroupProps) {
  return (
    <AriaCheckboxGroup
      value={value}
      onChange={onChange}
      isReadOnly={isReadOnly ?? false}
      isDisabled={isDisabled ?? false}
      className={cx(styles.checkGroup, className)}
      data-field={fieldName}
      data-testid={testId}
    >
      <FieldLabel label={label} />
      <div className={cx(styles.checkList, listClassName)}>{children}</div>
      <FieldHelp description={description} errorMessage={undefined} />
    </AriaCheckboxGroup>
  );
}

export interface RadioCardOption<V extends string> {
  value: V;
  title: string;
  description?: string;
  testId?: string;
}

export interface RadioCardsProps<V extends string> extends Omit<FieldChromeProps, 'label'> {
  /** The visible group label; omit it when a heading names the group (`aria-labelledby`). */
  label?: ReactNode;
  /** A visible heading that names the group instead of a label of its own. */
  'aria-labelledby'?: string;
  /** Visible text that describes the group (e.g. a lead line above it). */
  'aria-describedby'?: string;
  options: RadioCardOption<V>[];
  value: V;
  onChange: (value: V) => void;
  className?: string;
  isDisabled?: boolean;
  testId?: string;
}

/**
 * Exclusive choice shown as cards with a description (party type, ETA environment, the buyer's
 * e-mail copy). Native radio behaviour: arrow keys move AND choose.
 */
export function RadioCards<V extends string>({
  label,
  'aria-labelledby': labelledBy,
  'aria-describedby': describedBy,
  testId,
  description,
  errorMessage,
  isRequired,
  fieldName,
  options,
  value,
  onChange,
  className,
  isDisabled,
}: RadioCardsProps<V>) {
  return (
    <RadioGroup
      value={value}
      onChange={(next) => {
        const option = options.find((o) => o.value === next);
        if (option) onChange(option.value);
      }}
      isInvalid={Boolean(errorMessage)}
      isRequired={isRequired ?? false}
      isDisabled={isDisabled ?? false}
      validationBehavior="aria"
      className={cx(styles.radioGroup, className)}
      data-field={fieldName}
      data-testid={testId}
      {...(labelledBy ? { 'aria-labelledby': labelledBy } : {})}
      {...(describedBy ? { 'aria-describedby': describedBy } : {})}
    >
      {label === undefined ? null : <FieldLabel label={label} isRequired={isRequired ?? false} />}
      <div className={styles.radioList}>
        {options.map((option) => (
          <Radio
            key={option.value}
            value={option.value}
            className={styles.radioCard}
            data-testid={option.testId}
          >
            <span className={styles.radioDot} aria-hidden="true" />
            <span className={styles.radioText}>
              <span className={styles.radioTitle}>{option.title}</span>
              {option.description ? (
                <span className={styles.radioDescription}>{option.description}</span>
              ) : null}
            </span>
          </Radio>
        ))}
      </div>
      <FieldHelp description={description} errorMessage={errorMessage} />
    </RadioGroup>
  );
}

export interface SegmentedProps<V extends string> {
  'aria-label': string;
  options: { value: V; label: ReactNode; textLabel?: string }[];
  value: V;
  onChange: (value: V) => void;
  isDisabled?: boolean;
  className?: string;
}

/** Two or three exclusive modes (discount % / amount, customer / cash). */
export function Segmented<V extends string>({
  options,
  value,
  onChange,
  isDisabled,
  className,
  ...props
}: SegmentedProps<V>) {
  return (
    <ToggleButtonGroup
      {...props}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={[value]}
      onSelectionChange={(keys) => {
        const [first] = [...keys] as Key[];
        const option = options.find((o) => o.value === first);
        if (option) onChange(option.value);
      }}
      isDisabled={isDisabled ?? false}
      className={cx(styles.segmented, className)}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.value}
          id={option.value}
          className={styles.segment}
          {...(option.textLabel ? { 'aria-label': option.textLabel } : {})}
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

export interface ChoiceCardOption<V extends string> {
  value: V;
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  testId?: string;
}

export interface ChoiceCardsProps<V extends string> {
  /** Accessible name of the group (or `aria-labelledby` to a visible question). */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  options: ChoiceCardOption<V>[];
  /** `null`: nothing chosen yet (the batch page's task step); once chosen, never empty again. */
  value: V | null;
  onChange: (value: V) => void;
  isDisabled?: boolean;
  className?: string;
  testId?: string;
}

/**
 * Exclusive choice of a path, drawn as cards with a title and a one-line description
 * (DESIGN_SYSTEM §6). React Aria ToggleButtonGroup with one selection that is never empty: arrow
 * keys move focus between the cards; Enter, Space or a press chooses. An arrow never chooses.
 */
export function ChoiceCards<V extends string>({
  options,
  value,
  onChange,
  isDisabled,
  className,
  testId,
  ...props
}: ChoiceCardsProps<V>) {
  return (
    <ToggleButtonGroup
      {...props}
      selectionMode="single"
      disallowEmptySelection
      selectedKeys={value === null ? [] : [value]}
      onSelectionChange={(keys) => {
        const [first] = [...keys] as Key[];
        const option = options.find((o) => o.value === first);
        // Pressing the chosen card again changes nothing (and must not reset what it opened).
        if (option && option.value !== value) onChange(option.value);
      }}
      isDisabled={isDisabled ?? false}
      className={cx(styles.choiceCards, className)}
      data-testid={testId}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.value}
          id={option.value}
          className={styles.choiceCard}
          data-testid={option.testId}
        >
          {option.icon ? (
            <span className={styles.choiceIcon} aria-hidden="true">
              {option.icon}
            </span>
          ) : null}
          <span className={styles.radioText}>
            <span className={styles.radioTitle}>{option.title}</span>
            {option.description ? (
              <span className={styles.radioDescription}>{option.description}</span>
            ) : null}
          </span>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
